// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartStyleConfigurationFactories.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import Factories from './Izenda.Factories';
import reportPartStyleFactories from './Izenda.ReportPartStyleFactories';
import reportPartConfigFactories from './Izenda.ReportPartConfigurationFactories';
import { createPanelGroupSchema } from 'share/utils/Izenda.PropsEditorGuiSchemaFunctions';
import {
  negate,
  mergeAll,
  merge,
  constant,
  isArray,
  isPlainObject,
  isFunction,
  isNil,
  filter,
  has,
  get,
  getOr,
  compose,
  find,
  map,
  mapValues,
  includes
} from 'lodash/fp';

/**
 * A function that invokes an array of functions with the same params and return the merged result
 * @param {Array<Function>} funcs Array of functions that should be executed
 */
const mergeResults = (...funcs) => (...parms) =>
  funcs.map(fn => fn(...parms)).reduce((props, otherProps) => merge(otherProps, props), {});

/**
 * A function that invokes an array of functions with the same params and return the combined array
 * @param {Array<Function>} funcs Array of functions that should be executed
 */
const combineResults = (...funcs) => (...parms) =>
  funcs
    .map(fn => fn(...parms))
    .reduce(
      (props, otherProps) => (otherProps ? [...props, ...(isArray(otherProps) ? otherProps : [otherProps])] : props),
      []
    );

/**
 * A function that invokes an array of functions with the same params and return a Promise that merges the results
 * @param {Array<Function>} funcs Array of functions that should be executed
 */
const mergePromiseResult = (...funcs) => (...parms) => (...otherParams) =>
  Promise.reduce(
    funcs.map(fn => fn(...parms)),
    (props, fn) => {
      return fn(...otherParams).then(otherProps => merge(otherProps, props));
    },
    {}
  );

/**
 * A function that invokes an array of functions with the same params and return a Promise that merges the results
 * @param {Array<Function>} funcs Array of functions that should be executed
 */
const composePropertiesValueChanged = (
  onPropertiesValueChanged,
  onPropertiesValueChangedBase,
  onPropertiesValueChangedDefault
) => (...parms) => {
  const onValueChanged = onPropertiesValueChanged(...parms);
  const onValueChangedBase = onPropertiesValueChangedBase && onPropertiesValueChangedBase(...parms);
  const onValueChangedDefault = onPropertiesValueChangedDefault(...parms);
  const composeChanged = (
    schemaData,
    changedKey,
    changedKeyPath,
    changedValue,
    changedOthersInfo,
    derivedChange
  ) => chartProperties => {
    onValueChanged(chartProperties, schemaData, changedKey, changedKeyPath, changedValue, changedOthersInfo);
    derivedChange && derivedChange(chartProperties);
  };

  return (schemaData, changedKey, changedKeyPath, changedValue, changedOthersInfo, derivedChange) =>
    onValueChangedBase
      ? onValueChangedBase(
          schemaData,
          changedKey,
          changedKeyPath,
          changedValue,
          changedOthersInfo,
          composeChanged(schemaData, changedKey, changedKeyPath, changedValue, changedOthersInfo, derivedChange)
        )
      : onValueChangedDefault(
          schemaData,
          changedKey,
          changedKeyPath,
          changedValue,
          changedOthersInfo,
          composeChanged(schemaData, changedKey, changedKeyPath, changedValue, changedOthersInfo, derivedChange)
        );
};

/**
 * Convert schema object to defined schema using in Properties Editor
 * @param {Object} obj The schema object
 * @param {Function} defaultFactory Default factory function
 */
const convertSchemaObject = (obj, defaultFactory) =>
  Object.keys(obj).reduce((items, key) => {
    const { fields, factory = defaultFactory, ...itemProps } = obj[key];
    const item = factory(itemProps);

    fields && (item['fields'] = convertSchemaObject(fields, null));

    return Object.assign(items, { [key]: item });
  }, {});

/**
 * Create defined schema using in Properties Editor
 * @param {Object} schema Schema
 */
const createDefinedSchema = schema => {
  return {
    schemaKey: schema.schemaKey,
    fields: convertSchemaObject(schema.groups, createPanelGroupSchema)
  };
};

/**
 * Get a plain object with the properties that have key
 * @param {Object} mapping Object
 */
const getPropMappingObject = (mappings: Object): Object =>
  mapValues(
    mapping =>
      Object.keys(mapping)
        .filter(key => has('propKey', mapping[key]))
        .reduce(
          (propsMapping, key) =>
            Object.assign(propsMapping, {
              [key]: mapping[key] ? { key: mapping[key].propKey, source: mapping[key].propSource } : null
            }),
          {}
        ),
    mappings
  );

/**
 * Get a default value object from the mapping.
 * @param {Object} mapping Object
 */
const getPropDefaultObject = (mappings: Object): Object =>
  mapValues(
    mapping =>
      Object.keys(mapping)
        .filter(key => has('defaultValue', mapping[key]))
        .reduce((obj, key) => merge({ [key]: mapping[key].defaultValue }, obj), {}),
    mappings
  );

/**
 * Get the value of property of object until the value exists
 * @param {string} propName Property name to get value from
 * @param {Array<Object>} objs An array of object
 */
const getOrDefault = (propName: string, objs: Array<Object>): any =>
  compose(
    find(negate(isNil)),
    map(get(propName))
  )(objs);

/**
 * Generates key of report part type and style
 * @param {string} reportPartType Report part type
 * @param {string} reportPartStyle Report part style
 */
const composeKey = (reportPartType, reportPartStyle) => `${reportPartStyle}-${reportPartType}`;

const confFactories = new Factories('Report Part Style Configuration', true);

export default {
  /**
   * Extends a report part style configuration
   * @param {string} reportPartType The report part type
   * @param {string} reportPartStyle The new report part style
   * @param {string} baseStyle A chart type that new chart derives on
   * @param {Object} cfg New chart type configuration
   */
  extendConfiguration(reportPartType, reportPartStyle, baseStyle, cfg) {
    ensure(reportPartStyle, 'New report part style must have a value');
    ensure(
      !baseStyle || confFactories.containKey(composeKey(reportPartType, baseStyle)),
      `${baseStyle} doesn't exist. It needs to be register before extending`
    );
    ensure(cfg, 'New report part style configuration must have a value');

    const {
      fieldContainerSchema = [],
      propertyWidgets = {},
      propertySchema = {},
      propertyMappingProps = constant({}),
      propertyMappingSource = constant({}),
      propertyValueChange = () => () => {},
      optionsMapping = {}
    } = cfg;

    ensure(
      !fieldContainerSchema || isArray(fieldContainerSchema),
      'fieldContainerSchema property of configuration must be an array'
    );
    ensure(
      !propertyWidgets || isPlainObject(propertyWidgets),
      'propertyWidgets property of configuration must be a plain object'
    );
    ensure(
      !propertySchema || isPlainObject(propertySchema),
      'propertySchema property of configuration must be a plain object'
    );

    ensure(
      !propertyMappingProps || isFunction(propertyMappingProps),
      'propertyMappingProps property of configuration must be a function'
    );

    ensure(
      !propertyMappingSource || isFunction(propertyMappingSource),
      'propertyMappingSource property of configuration must be a function'
    );

    ensure(
      !propertyValueChange || isFunction(propertyValueChange),
      'propertyValueChange property of configuration must be a function'
    );

    ensure(
      !optionsMapping || isPlainObject(optionsMapping),
      'optionsMapping property of configuration must be a plan object'
    );

    // Provide function instead of the immediate object to avoid resolving base style configuration before the current style registration.
    const getBaseStyleCfg = baseStyle
      ? () => confFactories.create(composeKey(reportPartType, baseStyle))
      : () => ({
          propertyWidgets: {},
          propertySchema: {},
          propertyMappingProps: constant({}),
          propertyMappingSource: constant({}),
          optionsMapping: {}
        });

    const defaultConfig = reportPartConfigFactories.getConfiguration(reportPartType);

    // Store chart info in external factories in order to
    // easily and quickly enumerate the basic report part style information as well as
    // preventing trigger heavy computation on report part style configuration (ie. schema, mappings...)
    reportPartStyleFactories.add(
      reportPartType,
      reportPartStyle,
      baseStyle,
      getOrDefault('visualLabel', [cfg, defaultConfig]),
      getOrDefault('visualPreviewCSS', [cfg, defaultConfig])
    );

    // Register chart configurations
    const newCfg = confFactories.register(composeKey(reportPartType, reportPartStyle), () => {
      // Resolve the base style configuration
      const baseStyleCfg = getBaseStyleCfg();
      const configSeq = [cfg, baseStyleCfg, defaultConfig];
      const baseContainerSchema = baseStyleCfg.fieldContainerSchema
        ? [baseStyleCfg.fieldContainerSchema]
        : defaultConfig.fieldContainerSchema;

      const optionMappingsConfig = merge(defaultConfig.optionsMapping, optionsMapping);

      return {
        visualEngine: getOrDefault('visualEngine', configSeq),
        visualType: getOrDefault('visualType', configSeq),
        optionsBuilder: getOrDefault('optionsBuilder', configSeq),
        model: getOrDefault(
          'model',
          configSeq
        ) /* Each cfg contains an array of schema factory functions. It composes multiple schema factories into a single factory */,
        fieldContainerSchema: combineResults(...[...baseContainerSchema, ...fieldContainerSchema]),
        propertyWidgets: mergeAll([baseStyleCfg.propertyWidgets, defaultConfig.propertyWidgets], propertyWidgets),
        propertySchema: createDefinedSchema(
          mergeAll([baseStyleCfg.propertySchema, defaultConfig.propertySchema], propertySchema)
        ),
        propertyMappingProps: mergeResults(
          propertyMappingProps,
          baseStyleCfg.propertyMappingProps,
          defaultConfig.propertyMappingProps
        ),
        propertyMappingSource: mergeResults(
          propertyMappingSource,
          baseStyleCfg.propertyMappingSource,
          defaultConfig.propertyMappingSource
        ),
        propertyValueChange: composePropertiesValueChanged(
          propertyValueChange,
          baseStyleCfg.propertyValueChange,
          defaultConfig.propertyValueChange
        ),
        optionsMapping: merge(baseStyleCfg.optionsMapping, getPropMappingObject(optionMappingsConfig)),
        optionsDefault: merge(baseStyleCfg.optionsDefault, getPropDefaultObject(optionMappingsConfig))
      };
    });
  },

  /**
   * Get the configuration of report part style
   * @param {string} reportPartStyle Report part type
   */
  getConfiguration(reportPartType, reportPartStyle: string) {
    return confFactories.create(composeKey(reportPartType, reportPartStyle));
  },

  /**
   * Reset the factories data
   */
  reset() {
    reportPartStyleFactories.reset();
    confFactories.reset();
  }
};



// WEBPACK FOOTER //
// ./ui/share/factories/Izenda.ReportPartStyleConfigurationFactories.js